import type { DeviceModel } from '../../types/deviceModel';
import type { EquipmentFace, PortRect, SimpleLayout, MultiFaceLayout } from '../../types';

export type FacedPort = PortRect & { face: EquipmentFace };

export function collectLayoutPorts(layout?: {
  simple?: SimpleLayout;
  multiFace?: MultiFaceLayout;
}): FacedPort[] {
  if (!layout) return [];
  const out: FacedPort[] = [];
  const multi = layout.multiFace;
  if (multi?.faces) {
    (Object.keys(multi.faces) as EquipmentFace[]).forEach((face) => {
      const ports = multi.faces[face]?.ports || [];
      ports.forEach((p) => out.push({ ...p, face }));
    });
    if (out.length > 0) return out;
  }
  (layout.simple?.ports || []).forEach((p) => out.push({ ...p, face: 'front' }));
  return out;
}

export function collectModelPorts(model: DeviceModel | undefined | null): FacedPort[] {
  if (!model?.layout) return [];
  return collectLayoutPorts(model.layout);
}

export function layoutFromFacedPorts(
  ports: FacedPort[],
  fallback?: { simple?: SimpleLayout; multiFace?: MultiFaceLayout }
): { simple?: SimpleLayout; multiFace?: MultiFaceLayout } {
  const faces = new Set(ports.map((p) => p.face));
  if (faces.size === 1 && faces.has('front') && !fallback?.multiFace) {
    const simple = fallback?.simple;
    return {
      simple: {
        stage: simple?.stage || { width: 420, height: 70 },
        ports: ports.map(({ face: _f, ...p }) => p),
        templateSize: simple?.templateSize,
        blanks: simple?.blanks,
      },
    };
  }
  const multi: MultiFaceLayout = {
    defaultFace: fallback?.multiFace?.defaultFace || 'front',
    faces: {},
  };
  faces.forEach((face) => {
    const existing = fallback?.multiFace?.faces?.[face];
    multi.faces[face] = {
      stage: existing?.stage || { width: 420, height: 70 },
      ports: ports.filter((p) => p.face === face).map(({ face: _f, ...p }) => p),
      templateSize: existing?.templateSize,
      blanks: existing?.blanks,
    };
  });
  return { multiFace: multi };
}
